import { nextStepFor, hubspotDealUrl, pathwayCounts } from '../nextStep.js'
import styles from './nextStep.module.css'

/**
 * THE GUIDED NEXT STEP — the paint-by-numbers layer.
 *
 * A rep looking at a police department or a Greek chapter should never have to
 * work out what the move is. Every record in a recognition / pursuit space
 * carries exactly ONE next step, numbered against a fixed sequence, with a
 * one-line what-happens-next and the button that does it.
 *
 * The model lives in `nextStep.js` (pure, asserted by verify-spaces). This file
 * only renders it and routes the click:
 *
 *   `nominate` / `pursue`  → the create flow (`RecognitionModal`) — a real write
 *   `advance` / `assign` / `win` → the deal in HubSpot, where a HUMAN does it
 *
 * That split is the propose-don't-overwrite rule made visible: the Command
 * Center creates, and it never edits somebody's deal out from under them.
 */

/* ---- the full step, for a card or the drawer ------------------------- */

export default function NextStep({ record, action, onOpen, compact = false }) {
  const step = nextStepFor(record, {
    heroLabel: action?.heroLabel,
    honoreeNoun: action?.honoreeNoun,
    noun: action?.recordNoun,
  })
  if (!step) return null
  if (compact) return <NextStepCompact record={record} action={action} onOpen={onOpen} />

  return (
    <div className={`${styles.box} ${styles[step.tone] || ''}`} onClick={(e) => e.stopPropagation()}>
      <div className={styles.head}>
        <StepChip step={step} />
        <span className={styles.title}>{step.title}</span>
      </div>
      <p className={styles.what}>{step.what}</p>
      {step.cta && <StepButton step={step} record={record} action={action} onOpen={onOpen} />}
    </div>
  )
}

/* ---- the one-line version, for a dense table row --------------------- */

export function NextStepCompact({ record, action, onOpen }) {
  const step = nextStepFor(record, {
    heroLabel: action?.heroLabel,
    honoreeNoun: action?.honoreeNoun,
    noun: action?.recordNoun,
  })
  if (!step) return null
  return (
    <span className={`${styles.row} ${styles[step.tone] || ''}`} onClick={(e) => e.stopPropagation()} title={step.what}>
      <StepChip step={step} />
      {step.cta
        ? <StepButton step={step} record={record} action={action} onOpen={onOpen} small />
        : <span className={styles.rowDone}>{step.title}</span>}
    </span>
  )
}

/* ---- pieces ---------------------------------------------------------- */

function StepChip({ step }) {
  return (
    <span className={styles.chip} title={step.done ? 'Complete' : `Step ${step.step} of ${step.of}`}>
      {step.done ? '✓' : `${step.step}/${step.of}`}
    </span>
  )
}

/**
 * The button that does the thing.
 *
 * A step whose action is a WRITE opens the create flow. A step whose action is
 * a human decision on an existing deal renders as a real link to that deal in
 * HubSpot — and degrades to "open the record" when there is no deal id to link
 * to, rather than rendering a dead button.
 */
function StepButton({ step, record, action, onOpen, small = false }) {
  const cls = `${styles.btn} ${small ? styles.btnSm : ''}`
  const creates = step.cta.action === 'nominate' || step.cta.action === 'pursue'

  if (creates) {
    return (
      <button
        type="button"
        className={`${cls} ${styles.btnPrimary}`}
        onClick={(e) => { e.stopPropagation(); action?.onStart?.(record) }}
      >
        {step.cta.label} →
      </button>
    )
  }

  const url = hubspotDealUrl(step.cta.dealId)
  if (url) {
    return (
      <a
        className={cls}
        href={url}
        target="_blank"
        rel="noopener noreferrer"
        onClick={(e) => e.stopPropagation()}
        title="Opens the deal in HubSpot — the Command Center never edits it for you"
      >
        {step.cta.label} ↗
      </a>
    )
  }
  return (
    <button type="button" className={cls} onClick={(e) => { e.stopPropagation(); onOpen?.(record) }}>
      {step.cta.label} →
    </button>
  )
}

/* ---- the pathway bar (WHO to act on) --------------------------------- */

/**
 * THE SEQUENCE, ABOVE THE VIEWS. How many records sit at each step, in order,
 * so the space opens on "342 departments need a hero" rather than on a table.
 * Clicking a step filters the board to exactly those records.
 *
 * Every record lands in exactly one bucket, so the counts sum to the filtered
 * universe — the bar can never quietly drop one.
 */
export function PathwayBar({ records, kind, action, value = '', onSelect }) {
  const steps = pathwayCounts(records, kind, {
    heroLabel: action?.heroLabel,
    honoreeNoun: action?.honoreeNoun,
    noun: action?.recordNoun,
  })
  if (!steps.length) return null

  return (
    <div className={styles.bar}>
      <span className={styles.barLabel}>Next step</span>
      {steps.map((s, i) => (
        <button
          key={s.key}
          type="button"
          className={`${styles.barStep} ${value === s.key ? styles.barStepOn : ''}`}
          onClick={() => onSelect?.(value === s.key ? '' : s.key)}
          title={`${s.hint} · click to show only these`}
          disabled={!s.count && value !== s.key}
        >
          <span className={styles.barNum}>{i + 1}</span>
          <span className={styles.barCount}>{s.count.toLocaleString()}</span>
          <span className={styles.barText}>{s.label}</span>
        </button>
      ))}
      {value && (
        <button type="button" className={styles.barClear} onClick={() => onSelect?.('')}>Clear</button>
      )}
    </div>
  )
}
